import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useCounterpartySearch } from '@/hooks/counterparty';
import { useGenerateStatement } from '../hooks/use-generate-statement';

interface Props {
  onClose: () => void;
}

/**
 * Last completed month as `YYYY-MM`, computed in UTC to match the backend guard
 * (`timezone.now().date()`). Local time would offer/default a month the backend
 * rejects with 400 for a UTC+ trader in the first hours of a new UTC month.
 * Day 0 of the current UTC month = last day of the previous UTC month.
 */
const lastCompletedMonth = (): string => {
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const month = String(end.getUTCMonth() + 1).padStart(2, '0');
  return `${end.getUTCFullYear()}-${month}`;
};

/**
 * Manual statement generation (LR-510): pick a counterparty and a completed
 * month, POST to the generate endpoint, then the mutation invalidates the list.
 * The month input is capped at the last completed month; the backend rejects the
 * current/unclosed month with 400 regardless.
 */
export const GenerateStatementModal: React.FC<Props> = ({ onClose }) => {
  const maxMonth = useMemo(lastCompletedMonth, []);
  const [counterparty, setCounterparty] = useState('');
  // Kept rather than looked up: the loaded window moves with the query, so the
  // selection would blank out on the next keystroke (LR-572).
  const [counterpartyName, setCounterpartyName] = useState('');
  const [cpQuery, setCpQuery] = useState('');
  const [month, setMonth] = useState(maxMonth);
  const [emptyNotice, setEmptyNotice] = useState(false);
  const [deferredRepoCount, setDeferredRepoCount] = useState(0);
  const [finalizedNotice, setFinalizedNotice] = useState(false);
  const [magnitudeCount, setMagnitudeCount] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  // LR-572: searched and paged server-side instead of pulling the registry into
  // the browser. Only active counterparties: generating a statement for a
  // deactivated CP has no product meaning here (dp #4). The serializer still
  // accepts any id for a future final statement of a just-deactivated CP.
  const {
    options,
    isLoading: cpLoading,
    isError: cpError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCounterpartySearch(cpQuery, true);
  const { mutate, isPending, error } = useGenerateStatement();
  // LR-680: a 403 is a permissions problem — "check the counterparty and month"
  // would send the user hunting for a data mistake. Reachable even with the
  // button gated on `create_trades`: the client's capability snapshot is taken
  // once per page load (AuthGuard → /auth/me), so a capability revoked
  // server-side leaves the button on screen until the next reload, and the
  // backend stays the authority.
  const permissionDenied =
    (error as { response?: { status?: number } } | null)?.response?.status === 403;

  // Focus the dialog on mount so Escape works without tabbing into it first.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const canSubmit = counterparty !== '' && month !== '' && !isPending;

  const handleGenerate = () => {
    if (!canSubmit) return;
    setEmptyNotice(false);
    setDeferredRepoCount(0);
    setFinalizedNotice(false);
    setMagnitudeCount(0);
    mutate(
      { counterparty: Number(counterparty), period_start: `${month}-01` },
      {
        onSuccess: (result) => {
          // Some accruals were left unbilled behind a statement that is no longer a
          // draft (paid, or issued by hand in the admin), whose total isn't
          // rewritten. Warn and keep the modal open instead of closing as a silent
          // success on the re-queried statement. Magnitude goes first: both leave
          // accruals unbilled, but only it has a fix to ask for (a data repair).
          if (result.magnitude_defects > 0) {
            setMagnitudeCount(result.magnitude_defects);
            return;
          }
          if (result.unbilled_remaining) {
            setFinalizedNotice(true);
            return;
          }
          // Nothing to bill for this CP+month: keep the modal open and tell the
          // user rather than silently closing on an empty result. Everything but
          // a GMRA row counts as work done - since LR-552 generation never
          // produces a GMRA statement, so a GMRA row in the re-query is a
          // pre-LR-552 legacy artifact that was NOT (re)generated by this run.
          // A cash pool statement (blank `agreement`, LR-558) IS produced by this
          // run and counts; the skipped-because-already-finalized case is caught
          // above by `unbilled_remaining`, which since LR-558 covers both kinds.
          const generated = result.statements.filter((s) => s.agreement !== 'gmra');
          if (generated.length === 0) {
            setDeferredRepoCount(result.deferred_repo_trades);
            setEmptyNotice(true);
            return;
          }
          onClose();
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Generate billing statement"
        tabIndex={-1}
        // No overflow-hidden: the counterparty SearchableSelect drops its list as
        // an absolute child, and a clipping dialog cuts it at the modal edge.
        className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Generate Statement</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 font-medium">Counterparty</span>
            <SearchableSelect
              value={counterparty}
              onChange={(v, option) => {
                setCounterparty(v);
                setCounterpartyName(option?.label ?? '');
                setEmptyNotice(false);
                setFinalizedNotice(false);
              }}
              options={options}
              selectedLabel={counterpartyName || undefined}
              placeholder="Select counterparty"
              searchPlaceholder="Search counterparties…"
              // Not disabled while loading: the dropdown reports its own loading
              // state, and locking the control would block the search that fixes it.
              onSearchChange={setCpQuery}
              isLoading={cpLoading}
              isError={cpError}
              hasMore={hasNextPage}
              // `cancelRefetch: false` so a burst of scroll events joins the page
              // already in flight instead of cancelling and re-issuing it.
              onLoadMore={() => fetchNextPage({ cancelRefetch: false })}
              isLoadingMore={isFetchingNextPage}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700 font-medium">Month</span>
            <input
              type="month"
              value={month}
              max={maxMonth}
              onChange={(e) => {
                setMonth(e.target.value);
                setEmptyNotice(false);
                setFinalizedNotice(false);
              }}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-xs text-gray-400">
              Only completed months; the current month is view-only in the preview.
            </span>
          </label>

          {error && (
            <div className="text-sm text-red-600">
              {permissionDenied
                ? 'You do not have permission to generate statements.'
                : 'Failed to generate statement. Check the counterparty and month.'}
            </div>
          )}

          {emptyNotice && (
            <div className="text-sm text-amber-600">
              No billable accruals for this counterparty and month - nothing to generate.
              {/* Name repo as the cause only when it actually is one: the common
                  case is simply a month with no accruals, and blaming repo there
                  would send a trader hunting a misclassified GMSLA book. */}
              {deferredRepoCount > 0 && (
                <span className="mt-1 block text-xs text-gray-500">
                  {deferredRepoCount} repo (GMRA) trade{deferredRepoCount === 1 ? '' : 's'}{' '}
                  skipped: repo interest is realised in the Repurchase Price at
                  termination, so it never forms a monthly trade statement. Its
                  cash pool interest is billed on its own statement.
                </span>
              )}
            </div>
          )}

          {magnitudeCount > 0 && (
            <div className="text-sm text-amber-600">
              {magnitudeCount} accrual row{magnitudeCount === 1 ? '' : 's'} withheld:
              the stored amount is a clean power of ten off the row&apos;s own rate.
              <span className="mt-1 block text-xs text-gray-500">
                Re-running will withhold {magnitudeCount === 1 ? 'it' : 'them'} again
                - the rows have to be corrected first. Ask the backend team to run
                the accrual magnitude audit.
              </span>
            </div>
          )}

          {finalizedNotice && (
            <div className="text-sm text-amber-600">
              A statement for this month is no longer a draft; late accruals were left
              unbilled and can&apos;t be regenerated (corrections come later).
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isPending ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};
